/* eslint-disable indent */
import fileDownload from 'js-file-download'

import { Logger, LoggerTypes, clearData } from '../utils'
import { BASE_API_URL } from '../constants'
import { customAxios } from './customAxios'

const method = {
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
  PUT: 'PUT'
}

const ONE_SECOND = 1000

export class Api {
  constructor(endpoint) {
    this.endpoint = endpoint
    this.client = customAxios

    this.client.baseURL = BASE_API_URL
  }

  /**
   * Send an HTTP / GET message to the API to a specific `endpoint`.
   *
   * @returns {Promise<AxiosResponse<never>>}
   */
  async get() {
    const endpoint = this.endpoint

    Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.get(BEFORE)', {
      url: `${BASE_API_URL}${endpoint}`,
      client: this.client
    })

    const result = await this.client.get(endpoint, { baseURL: BASE_API_URL })

    Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.get(AFTER)', {
      method: method.GET,
      url: `${BASE_API_URL}${endpoint}`,
      result
    })

    return result
  }

  /**
   * Send an HTTP / POST message to the API to a specific `endpoint`.
   *
   * @param {object} data to POST to the API.
   * @param {object} config OPTIONAL request header configuration.
   * @returns {Promise<AxiosResponse<never>>}
   */
  async create(data, config) {
    const endpoint = this.endpoint
    const _clearData = clearData(data)
    const _config = {
      baseURL: BASE_API_URL,
      headers: {
        'Content-Type': 'application/json'
      },
      ...config
    }

    Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.create(BEFORE)', {
      url: `${BASE_API_URL}${endpoint}`,
      config,
      data,
      _clearData
    })

    if (_clearData && _clearData !== {}) {
      const result = await this.client.post(endpoint, _clearData, _config)

      Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.create(AFTER)', {
        method: method.POST,
        url: `${BASE_API_URL}${endpoint}`,
        result
      })

      return result
    }
    return null
  }

  /**
   * Send an HTTP / PUT message to the API to a specific `endpoint`.
   *
   * @param {object} data to PUT to the API.
   * @param {object} config OPTIONAL request header configuration.
   * @returns {Promise<AxiosResponse<never>>}
   */
  async update(data, config) {
    const endpoint = this.endpoint
    const _clearData = clearData(data)
    const _config = {
      baseURL: BASE_API_URL,
      headers: {
        'Content-Type': 'application/json'
      },
      ...config
    }

    Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.update(BEFORE)', {
      url: `${BASE_API_URL}${endpoint}`,
      config,
      data,
      _clearData
    })

    if (_clearData && _clearData !== {}) {
      const result = await this.client.put(endpoint, _clearData, _config)

      Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.update(AFTER)', {
        method: method.PUT,
        url: `${BASE_API_URL}${endpoint}`,
        result
      })

      return result
    }
    return null
  }

  /**
   * Send an HTTP / DELETE message to the API to a specific `endpoint`.
   *
   * @returns {Promise<AxiosResponse<never>>}
   */
  async delete() {
    const endpoint = this.endpoint

    Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.delete(BEFORE)', { url: `${BASE_API_URL}${endpoint}` })

    const result = await this.client.delete(endpoint, { baseURL: BASE_API_URL })

    Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.delete(AFTER)', {
      method: method.DELETE,
      url: `${BASE_API_URL}${endpoint}`,
      result
    })

    return result
  }

  /**
   * Send an HTTP / GET message to the API to a specific `endpoint`.
   *
   * @param {string} filename Name of the file to download.
   * @returns {FILE}
   */
  async download(filename) {
    const endpoint = this.endpoint

    Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.download(BEFORE)', { url: `${BASE_API_URL}${endpoint}` })

    this.client
      .get(endpoint, {
        baseURL: BASE_API_URL,
        timeout: ONE_SECOND * 60 * 1, // <-- 1 minutes max to download a file
        responseType: 'blob'
      })
      .then((result) => {
        Logger.trace(LoggerTypes.API, '[MIDDLEWARE] Api.download(AFTER)', {
          method: method.GET,
          url: `${BASE_API_URL}${endpoint}`,
          result
        })
        fileDownload(result.data, filename)
      })
      .catch((err) => {
        Logger.trace(LoggerTypes.WARN, '[MIDDLEWARE] Api.download(WARN)', { err })
      })
  }
}
